import Images from 'app/assets/img';
import { StyledStack } from 'app/components/Stack';
import StyledButton from 'app/components/StyledButton';
import useGetApiData from 'app/hooks/useGetApiData';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import {
  cancelTopupDepositQR,
  getAutoDepositDetail,
} from 'app/services/authApi';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { formatNumber } from 'utils/helper';
import { useSearchParams } from 'utils/hooks/useSearchParams';
import PaymentResult from './PaymentResult';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { ReactComponent as CopyIcon } from 'app/assets/img/copy-icon.svg';
import { notify } from 'reapop';
import { Divider } from 'antd';

export enum DepositStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  PENDING = 'pending',
  CANCELLED = 'cancelled',
}
export const TOP_UP_METHODS = {
  MB: 'mb',
  NCB: 'ncb',
} as const;
export type TopUpMethod = typeof TOP_UP_METHODS[keyof typeof TOP_UP_METHODS];
export const isValidTopUpMethod = (value: any): value is TopUpMethod => {
  return Object.values(TOP_UP_METHODS).includes(value);
};
export const getTopUpMethod = (value: any): TopUpMethod => {
  return isValidTopUpMethod(value) ? value : TOP_UP_METHODS.NCB;
};

export default function PaymentDetail() {
  const infoUser = useSelector(selectInfoUser);
  const { getUrlParam } = useSearchParams();
  const id = getUrlParam('id');
  const viet_qr_content = getUrlParam('viet_qr_content');
  const account_no = getUrlParam('account_no');
  const account_name = getUrlParam('account_name');
  const amount = getUrlParam('amount');
  const transactionId = getUrlParam('transaction_id');
  const bankName = getUrlParam('bankName');
  const qrRef = useRef<HTMLCanvasElement>(null);
  const history = useHistory();
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const dispatch = useDispatch();

  const translations = useSelector(selectTranslations);
  const {
    data,
    handleRequest: handleRequestGetStatus,
    error,
  } = useGetApiData({
    api: getAutoDepositDetail,
    params: id,
    isSkip: !id,
  });

  const [isCancelQR, setIsCancelQR] = useState(false);
  const handleCancelQR = () => {
    const onCancelQR = async () => {
      try {
        setIsCancelQR(true);
        await cancelTopupDepositQR();
        history.replace('/auto-deposit');
      } catch (err) {
      } finally {
        setIsCancelQR(false);
      }
    };

    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: (
        <span
          style={{
            textAlign: 'center',
            display: 'block',
            fontSize: 20,
            color: '#1c1b1f',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
          dangerouslySetInnerHTML={{
            __html:
              translations?.['b2b.txt.cfRemoveTopUpQRCode'] ??
              'Khi bạn tạo mới giao dịch Nạp tiền mới, giao dịch nạp tiền hiện tại sẽ bị hủy',
          }}
        />
      ),
      closable: false,
      buttons: [
        {
          text: translations?.['b2b.btn.cancel'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.btn.confirm'] ?? '',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: () => {
            onCancelQR();
            onCloseDialogNoti();
          },
        },
      ],
    });
  };

  useEffect(() => {
    if (!!error)
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: error,
        content: '',
        closable: true,
        onCloseDialog: () => {
          onCloseDialogNoti();
          history.replace('/auto-deposit');
        },
      });
  }, [error, history, onCloseDialogNoti, onOpenDialogNoti]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (id) {
        handleRequestGetStatus(id);
      }
    }, 10000);
    return () => {
      clearInterval(intervalId);
    };
  }, [id, handleRequestGetStatus]);

  const handleDownloadImage = async () => {
    try {
      if (!qrRef.current) return;
      // Convert the canvas to a data URL
      const dataUrl = qrRef.current.toDataURL('image/png');

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${data?.qrCode?.account_name}-${data?.qrCode?.account_no}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  if (data?.status === DepositStatus.SUCCESS) {
    return <PaymentResult />;
  }
  const topUpMethod = getTopUpMethod(infoUser?.businessPartner?.topUpMethod);
  return (
    <LoadingContainer loading={isCancelQR}>
      <StyledStack direction={'column'} rowGap={'16px'} alignItem={'center'}>
        <QrContainer
          className="qr-container"
          height={{ lg: 'auto', xs: 'auto' }}
          width={{ lg: '337px', xs: '100%' }}
        >
          <StyledStack direction={'column'} alignItem={'center'} rowGap={'6px'}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ marginBottom: 5 }}
            >
              {topUpMethod === TOP_UP_METHODS.MB ? (
                <img src={Images.iconMBLogo} alt="mb logo" height={23} />
              ) : (
                <img src={Images.iconNcbLogo} alt="ncb logo" height={23} />
              )}
            </div>
            <div>
              <QRCodeCanvas
                value={data?.qrCode?.viet_qr_content || viet_qr_content}
                level="H"
                size={156}
                ref={qrRef}
              />
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <img
                src={Images.vietQRPay}
                alt="vietQRPay logo"
                style={{ width: 100, height: 45 }}
              />
              <Divider
                type="vertical"
                style={{ backgroundColor: '#adacb0', height: 20 }}
              />
              <img
                src={Images.vietQRGlobal}
                alt="vietQRGlobal logo"
                style={{ width: 90, marginRight: 10, marginLeft: 10 }}
              />
            </div>
          </StyledStack>
          <div>
            <QRLabelText style={{ marginTop: 4 }}>
              {translations?.['b2c.txt.bank']}
            </QRLabelText>
            <QRDescriptionText>
              {data?.qrCode?.bankName || bankName}
            </QRDescriptionText>
            <QRLabelText>
              {translations?.['b2b.autoDeposit.accountOwner']}
            </QRLabelText>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ marginTop: 2 }}
            >
              <QRDescriptionText>
                {data?.qrCode?.account_no || account_no}
              </QRDescriptionText>
              <CopyIcon
                style={{
                  cursor: 'pointer',
                  width: 16,
                  height: 16,
                  margin: '0px 0px 0px 8px',
                }}
                onClick={() => {
                  navigator.clipboard.writeText(
                    (data?.qrCode?.account_no || account_no) ?? '',
                  );
                  dispatch(
                    notify('', 'success', {
                      title: translations?.['b2b.txt.copied'] ?? 'Đã sao chép',
                      dismissAfter: 2000,
                    }),
                  );
                }}
              />
            </div>
            <QRDescriptionText>
              {data?.qrCode?.account_name || account_name}
            </QRDescriptionText>
            <QRLabelText style={{ marginTop: 4 }}>
              {translations?.['b2b.autoDeposit.content']}
            </QRLabelText>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ marginTop: 2 }}
            >
              <QRDescriptionText>{data?.id || transactionId}</QRDescriptionText>
              <CopyIcon
                style={{
                  cursor: 'pointer',
                  width: 16,
                  height: 16,
                  margin: '0px 0px 0px 8px',
                }}
                onClick={() => {
                  navigator.clipboard.writeText(
                    (data?.id || transactionId) ?? '',
                  );
                  dispatch(
                    notify('', 'success', {
                      title: translations?.['b2b.txt.copied'] ?? 'Đã sao chép',
                      dismissAfter: 2000,
                    }),
                  );
                }}
              />
            </div>
          </div>
          <QRPriceText>
            đ{formatNumber(data?.amount || Number(amount))}
          </QRPriceText>
        </QrContainer>
        <NoteText
          dangerouslySetInnerHTML={{
            __html: translations?.['b2b.autoDeposit.scanToPay'] ?? '',
          }}
        />
        <StyledStack
          justifyContent={'center'}
          columnGap={'9px'}
          rowGap={'9px'}
          direction={{ md: 'row', xs: 'column' }}
          alignItem={'center'}
        >
          <StyledButton
            textColor="white"
            style={{ minWidth: 206, width: 'fit-content' }}
            onClick={handleCancelQR}
          >
            {translations?.['b2b.autoDeposit.newTopUp']}
          </StyledButton>
          <StyledButton
            style={{ minWidth: 206, width: 'fit-content' }}
            onClick={handleDownloadImage}
            textColor="white"
          >
            {translations?.['b2b.autoDeposit.downloadQr']}
          </StyledButton>
        </StyledStack>
      </StyledStack>
    </LoadingContainer>
  );
}

// Update the QrContainer to include a className
const QrContainer = styled(StyledStack)`
  flex-direction: column;
  align-items: center;
  border: 2px solid #ff6b34;
  border-radius: 6px;
  padding-top: 15px;
  overflow: hidden;
  gap: 6px;
  &.qr-container {
    background: white;
  }
`;

const QRLabelText = styled.p`
  margin: 0;
  font-size: 9px;
  color: #adacb0;
  font-weight: 500;
  text-align: center;
  line-height: 16px;
`;

const QRDescriptionText = styled.p`
  margin: 0;
  font-size: 11px;
  color: #000;
  font-weight: 500;
  text-align: center;
  line-height: 16px;
`;

const QRPriceText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 21px;
  color: #000;
  font-weight: 600;
  text-align: center;
  padding: 9px 12px;
  background: #f8f8f8;
  width: 100%;
  line-height: 16px;
`;

const NoteText = styled.span`
  font-size: 12px;
  color: #000;
  font-weight: 500;
  text-align: center;
  line-height: 16px;
`;
